<template>
    <TransitionRoot appear as="template" :show="isVisible">
        <Dialog as="div" class="relative z-[10000]" @close="closeDialog">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
                leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4" @click.stop>
                    <TransitionChild as="template" enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel
                            class="relative transform overflow-hidden rounded-lg bg-gray-800 shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                            <div class="bg-gray-800">
                                <div class="flex items-center justify-between p-6 border-b border-gray-600">
                                    <h3 class="text-lg font-semibold leading-6 text-white">
                                        {{ isEditMode ? 'Edit Blog' : 'Add Blog' }}
                                    </h3>
                                    <button type="button" @click="closeDialog" :disabled="isLoading"
                                        class="rounded-md text-gray-400 hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                                        <span class="sr-only">Close</span>
                                        <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div class="p-6">
                                    <div class="space-y-6">
                                        <div>
                                            <label class="block text-sm font-medium text-gray-200 mb-1">Title *</label>
                                            <input v-model="formData.title" type="text" maxlength="200"
                                                class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                :class="{ 'border-red-400': errors.title }" />
                                            <p v-if="errors.title" class="mt-1 text-sm text-red-400">{{ errors.title }}
                                            </p>
                                        </div>

                                        <div>
                                            <label class="block text-sm font-medium text-gray-200 mb-1">Image</label>
                                            <div class="mt-2">
                                                <div
                                                    class="flex flex-col sm:flex-row items-start sm:items-center sm:space-x-3 space-y-3 sm:space-y-0 w-full">
                                                    <input ref="fileInputRef" type="file" accept="image/*"
                                                        @change="onImageSelected"
                                                        :disabled="isLoading || imageUploading" class="sr-only" />

                                                    <div role="button" tabindex="0" @click="triggerFileSelect"
                                                        @keydown.enter="triggerFileSelect"
                                                        class="w-full h-48 sm:h-36 rounded-md border-2 border-dashed border-gray-600 bg-gray-800 flex items-center justify-center cursor-pointer overflow-hidden">
                                                        <template v-if="previewUrl">
                                                            <img :src="previewUrl" alt="preview"
                                                                class="w-full h-full object-cover" />
                                                        </template>
                                                        <template v-else>
                                                            <div class="text-center text-gray-400">
                                                                <svg xmlns="http://www.w3.org/2000/svg"
                                                                    class="mx-auto h-8 w-8" fill="none"
                                                                    viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                                        stroke-width="2"
                                                                        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7M16 3l-4 4-4-4M12 7v10" />
                                                                </svg>
                                                                <div class="text-sm mt-1">Click to select image</div>
                                                            </div>
                                                        </template>
                                                    </div>

                                                    <!-- <div v-if="imageUploading" class="text-sm text-gray-300">Uploading:
                                                        {{ imageUploadProgress }}%</div> -->
                                                </div>
                                            </div>

                                            <div class="mt-4">
                                                <label class="block text-sm font-medium text-gray-200 mb-1">Description
                                                    (HTML)</label>
                                                <div class="mb-2 space-x-2">
                                                    <button type="button"
                                                        class="px-2 py-1 bg-gray-600 text-white rounded"
                                                        @click="format('bold')">B</button>
                                                    <button type="button"
                                                        class="px-2 py-1 bg-gray-600 text-white rounded"
                                                        @click="format('italic')">I</button>
                                                    <button type="button"
                                                        class="px-2 py-1 bg-gray-600 text-white rounded"
                                                        @click="format('underline')">U</button>
                                                    <button type="button"
                                                        class="px-2 py-1 bg-gray-600 text-white rounded"
                                                        @click="format('insertUnorderedList')">•
                                                        List</button>
                                                    <button type="button"
                                                        class="px-2 py-1 bg-gray-600 text-white rounded"
                                                        @click="format('insertOrderedList')">1.
                                                        List</button>
                                                    <button type="button"
                                                        class="px-2 py-1 bg-gray-600 text-white rounded"
                                                        @click="addLink">Link</button>
                                                </div>
                                                <div ref="editorRef" contenteditable="true"
                                                    class="editor-content min-h-[140px] w-full bg-gray-700 border border-gray-600 rounded-md p-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 overflow-auto"
                                                    @input="onEditorInput"></div>
                                                <p v-if="errors.description" class="mt-1 text-sm text-red-400">{{
                                                    errors.description }}</p>
                                            </div>
                                        </div>

                                        <!-- check-box -->
                                        <div>
                                            <label class="block text-sm font-medium text-gray-200 mb-1">Status</label>
                                            <div class="flex items-center">
                                                <div role="switch" :aria-checked="formData.status" tabindex="0"
                                                    @click="formData.status = !formData.status"
                                                    @keydown.enter.prevent="formData.status = !formData.status"
                                                    @keydown.space.prevent="formData.status = !formData.status" :class="{
                                                        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none': true,
                                                        'bg-blue-600': formData.status === true,
                                                        'bg-gray-600': formData.status === false,
                                                    }">
                                                    <span :class="{
                                                        'inline-block h-4 w-4 transform rounded-full bg-white transition-transform': true,
                                                        'translate-x-5': formData.status === true,
                                                        'translate-x-1': formData.status === false,
                                                    }"></span>
                                                </div>
                                                <span class="ml-3 text-sm text-gray-300">{{ formData.status ? 'Active' :
                                                    'Inactive' }}</span>
                                            </div>
                                            <p v-if="errors.status" class="mt-1 text-sm text-red-400">{{ errors.status
                                                }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="bg-gray-700 px-6 py-4 flex justify-end space-x-3">
                                    <button @click="closeDialog" type="button" :disabled="isLoading"
                                        class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 border border-gray-500 rounded-md shadow-sm hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
                                        Cancel
                                    </button>
                                    <button @click="handleSubmit" type="button" :disabled="isLoading || imageUploading"
                                        class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
                                        <template v-if="isLoading">
                                            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none"
                                                viewBox="0 0 24 24">
                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                                    stroke-width="4" />
                                                <path class="opacity-75" fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            Saving...
                                        </template>
                                        <template v-else>{{ isEditMode ? 'Update' : 'Create' }}</template>
                                    </button>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { projectFirestore, projectStorage, projectAuth } from '@/firebase/config';
import { useAuthStore } from '@/stores/auth';
import { doc, setDoc, getDoc, serverTimestamp, collection } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import {
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue';

const props = defineProps({
    isVisible: {
        type: Boolean,
        default: false,
    },
    // blogId is the document id for the blog being edited. null when creating.
    blogId: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['onEmitHandleEvent']);

const authStore = useAuthStore();
const isLoading = ref(false);
const imageUploading = ref(false);
const formData = ref({
    title: '',
    imageURL: '',
    description: '', // store HTML
    status: true,
    createdAt: null,
    createdBy: '',
    updatedAt: null,
    updatedBy: '',
});
const errors = ref({});

const isEditMode = computed(() => !!props.blogId);

const previewUrl = ref('');
const selectedFile = ref(null);
const fileInputRef = ref(null);
let previewIsObjectUrl = false;
const imageUploadProgress = ref(0);

const closeDialog = () => {
    formData.value = {
        title: '',
        imageURL: '',
        description: '',
        status: true,
        createdAt: null,
        createdBy: '',
        updatedAt: null,
        updatedBy: '',
    };
    previewUrl.value = '';
    imageUploadProgress.value = 0;
    selectedFile.value = null;
    if (fileInputRef.value) fileInputRef.value.value = null;
    if (previewIsObjectUrl && previewUrl.value) {
        try { URL.revokeObjectURL(previewUrl.value); } catch (e) { }
        previewIsObjectUrl = false;
    }
    emit('onEmitHandleEvent', 'close');
};

const validateForm = () => {
    errors.value = {};
    let isValid = true;

    if (!formData.value.title.trim()) {
        errors.value.title = 'Title is required.';
        isValid = false;
    }

    // Description length check on plain text inside HTML
    const textOnly = (formData.value.description || '').replace(/<[^>]*>?/gm, '').trim();
    if (!textOnly) {
        errors.value.description = 'Description is required.';
        isValid = false;
    } else if (textOnly.length > 5000) {
        errors.value.description = 'Description is too long.';
        isValid = false;
    }

    return isValid;
};

const handleSubmit = async () => {
    if (isLoading.value || imageUploading.value) return;

    if (!validateForm()) return;

    isLoading.value = true;
    try {
        // If user selected a new file, upload it now and get the URL
        if (selectedFile.value) {
            const uploadedUrl = await uploadImage(selectedFile.value);
            if (uploadedUrl) {
                formData.value.imageURL = uploadedUrl;
            }
        }

        const currentUser = projectAuth.currentUser;
        if (!currentUser) throw new Error('No authenticated user found.');

        const blogData = {
            title: formData.value.title.trim(),
            imageURL: formData.value.imageURL || '',
            description: formData.value.description || '',
            status: formData.value.status,
            updatedAt: serverTimestamp(),
            updatedBy: currentUser.uid,
        };

        if (!isEditMode.value) {
            blogData.createdAt = serverTimestamp();
            blogData.createdBy = currentUser.uid;
        }

        const blogRef = doc(projectFirestore, 'blogs', props.blogId || doc(collection(projectFirestore, 'blogs')).id);

        // If editing, verify ownership to avoid permission denied from Firestore rules
        if (isEditMode.value) {
            const existing = await getDoc(blogRef);
            if (!existing.exists()) throw new Error('Blog not found');
            const data = existing.data() || {};
            if (!data.ownerId) throw new Error('Cannot edit this blog: owner not set on the document.');
            const uid = (projectAuth && projectAuth.currentUser && projectAuth.currentUser.uid)
                || authStore.userId
                || localStorage.getItem('userId');
            if (!uid || uid !== data.ownerId) {
                throw new Error('You do not have permission to edit this blog.');
            }
        }

        await setDoc(blogRef, blogData, { merge: isEditMode.value });

        emit('onEmitHandleEvent', isEditMode.value ? 'updated' : 'created');
    } catch (error) {
        console.error('Error saving category:', error);
        emit('onEmitHandleEvent', 'error', true, 'Failed to save blog. Please try again.');
    } finally {
        isLoading.value = false;
        imageUploading.value = false;
        // cleanup local preview URL and file input
        if (previewIsObjectUrl && previewUrl.value) {
            try { URL.revokeObjectURL(previewUrl.value); } catch (e) { }
            previewIsObjectUrl = false;
        }
        selectedFile.value = null;
        if (fileInputRef.value) fileInputRef.value.value = null;
    }
};

const fetchBlog = async () => {
    if (!props.blogId) return;
    try {
        const blogRef = doc(projectFirestore, 'blogs', props.blogId);
        const blogDoc = await getDoc(blogRef);
        if (blogDoc.exists()) {
            const data = blogDoc.data();
            formData.value = { ...formData.value, ...data };
            // ensure status defaults to true when missing
            if (typeof formData.value.status === 'undefined' || formData.value.status === null) {
                formData.value.status = true;
            }
            previewUrl.value = formData.value.imageURL || '';
            // populate editor HTML after next tick
            nextTick(() => {
                if (editorRef.value) editorRef.value.innerHTML = formData.value.description || '';
            });
        } else {
            emit('onEmitHandleEvent', 'error', true, 'Blog not found.');
        }
    } catch (error) {
        console.error('Error fetching blog:', error);
        emit('onEmitHandleEvent', 'error', true, 'Failed to load blog data.');
    }
};

const editorRef = ref(null);
let savedRange = null;

const saveSelection = () => {
    try {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
            savedRange = sel.getRangeAt(0).cloneRange();
        }
    } catch (e) {
        // ignore
    }
};

const restoreSelection = () => {
    try {
        const sel = window.getSelection();
        if (!sel) return;
        sel.removeAllRanges();
        if (savedRange) sel.addRange(savedRange);
    } catch (e) {
        // ignore
    }
};

onMounted(() => {
    // keep editorRef in sync
    if (editorRef.value && formData.value.description) editorRef.value.innerHTML = formData.value.description;

    // attach listeners to track selection/range so toolbar commands work even when editor loses focus
    nextTick(() => {
        if (!editorRef.value) return;
        editorRef.value.addEventListener('keyup', saveSelection);
        editorRef.value.addEventListener('mouseup', saveSelection);
        editorRef.value.addEventListener('blur', saveSelection);
    });
});

watch(
    () => props.blogId,
    (newId) => {
        if (newId) {
            fetchBlog();
        } else {
            // Reset formData and errors when switching to add mode
            formData.value = {
                title: '',
                imageURL: '',
                description: '',
                status: true,
                createdAt: null,
                createdBy: '',
                updatedAt: null,
                updatedBy: '',
            };
            errors.value = {};
            previewUrl.value = '';
            imageUploadProgress.value = 0;
            nextTick(() => {
                if (editorRef.value) editorRef.value.innerHTML = '';
            });
        }
    },
    { immediate: true }
);

// Image upload handlers
const onImageSelected = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    // Revoke previous object URL if any
    if (previewIsObjectUrl && previewUrl.value) {
        try { URL.revokeObjectURL(previewUrl.value); } catch (e) { }
        previewIsObjectUrl = false;
    }

    // store file locally and show preview, but don't upload yet
    selectedFile.value = file;
    try {
        const url = URL.createObjectURL(file);
        previewUrl.value = url;
        previewIsObjectUrl = true;
    } catch (err) {
        previewUrl.value = '';
        previewIsObjectUrl = false;
    }
};

const triggerFileSelect = () => {
    if (fileInputRef.value && !isLoading.value && !imageUploading.value) {
        fileInputRef.value.click();
    }
};

const uploadImage = (file) => {
    return new Promise((resolve, reject) => {
        if (!file) return resolve(null);
        imageUploading.value = true;
        const id = Date.now().toString();
        const storage_path = `blogs/${id}_${file.name}`;
        const sRef = storageRef(projectStorage, storage_path);
        const uploadTask = uploadBytesResumable(sRef, file);

        uploadTask.on('state_changed', (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            imageUploadProgress.value = progress;
        }, (err) => {
            console.error('Upload error', err);
            imageUploading.value = false;
            emit('onEmitHandleEvent', 'error', true, 'Image upload failed.');
            reject(err);
        }, async () => {
            try {
                const url = await getDownloadURL(uploadTask.snapshot.ref);
                imageUploading.value = false;
                resolve(url);
            } catch (err) {
                imageUploading.value = false;
                reject(err);
            }
        });
    });
};

// Editor helpers
const onEditorInput = (e) => {
    // Always read the HTML from the editor DOM node to avoid v-model reactivity clobbering caret
    if (editorRef.value) {
        formData.value.description = editorRef.value.innerHTML;
        // update saved selection after input
        saveSelection();
    } else {
        formData.value.description = e.target.innerHTML;
    }
};

const format = (cmd) => {
    // restore selection so execCommand applies to the editor content
    if (editorRef.value) editorRef.value.focus();
    restoreSelection();
    document.execCommand(cmd, false, null);
    // update model
    if (editorRef.value) {
        formData.value.description = editorRef.value.innerHTML;
        // save again because execCommand may change selection
        saveSelection();
    }
};

const addLink = () => {
    const url = prompt('Enter URL');
    if (!url) return;

    if (editorRef.value) editorRef.value.focus();
    restoreSelection();
    document.execCommand('createLink', false, url);

    // After creating the link, ensure all anchors in editor have consistent styling and attributes
    try {
        const anchors = editorRef.value.querySelectorAll('a');
        anchors.forEach(a => {
            // only modify anchors with non-javascript hrefs
            const href = (a.getAttribute('href') || '').trim();
            if (!href.toLowerCase().startsWith('javascript:')) {
                a.classList.add('editor-link');
                a.setAttribute('target', '_blank');
                a.setAttribute('rel', 'noopener noreferrer');
            }
        });
    } catch (e) {
        // ignore
    }

    if (editorRef.value) formData.value.description = editorRef.value.innerHTML;
    // save selection state
    saveSelection();
};
</script>

<style scoped>
input,
select,
textarea {
    transition: border-color 0.2s ease, background-color 0.2s ease;
}

input:focus,
select:focus,
textarea:focus {
    border-color: #3b82f6;
    /* blue-500 */
}

button:disabled {
    cursor: not-allowed;
}

/* Editor content styles */
.editor-content ul {
    list-style-type: disc;
    margin-left: 1.5rem;
    padding-left: 0.5rem;
}

.editor-content ol {
    list-style-type: decimal;
    margin-left: 1.5rem;
    padding-left: 0.5rem;
}

.editor-content li {
    margin: 0.25rem 0;
}

.editor-content ul ul {
    list-style-type: circle;
}

.editor-content ul ul ul {
    list-style-type: square;
}

.editor-content ol ol {
    list-style-type: lower-alpha;
}

.editor-content ol ol ol {
    list-style-type: lower-roman;
}

/* Editor link highlight */
.editor-content .editor-link {
    color: #2563eb;
    /* blue-600 */
    background-color: rgba(37, 99, 235, 0.12);
    /* translucent blue */
    padding: 0.18rem 0.8rem;
    /* increased horizontal padding */
    border-radius: 0.375rem;
    /* 6px */
    text-decoration: none;
    transition: background-color 150ms ease, color 150ms ease;
}

.editor-content ::v-deep .editor-link {
    color: #2563eb;
    /* blue-600 */
    background-color: rgba(169, 192, 240, 0.603);
    /* translucent blue */
    padding: 0.18rem 0.8rem;
    /* increased horizontal padding */
    border-radius: 0.375rem;
    /* 6px */
    text-decoration: none;
    transition: background-color 150ms ease, color 150ms ease;
}

.editor-content ::v-deep .editor-link:focus,
.editor-content ::v-deep .editor-link:hover {
    background-color: rgba(37, 99, 235, 0.20);
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}

.editor-content .editor-link:focus,
.editor-content .editor-link:hover {
    background-color: rgba(37, 99, 235, 0.20);
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}
</style>